/**
 * AuditChecklistTab — Main checklist tab container
 *
 * Composes SummaryStrip, ChecklistFilterBar, and grouped List of ChecklistRows.
 * Manages selected state and connects to data hooks.
 *
 * Outer component provides the Filter context; inner component consumes it.
 */

import { useState, useCallback, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { Circle } from "lucide-react";
import { List, springs, Filter, useFilter } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { useAuditDetail } from "@/hooks/use-audit-detail";
import { useChecklistData } from "@/hooks/use-checklist-data";
import type { ChecklistStats } from "@/hooks/use-checklist-data";
import type { UIChecklistItem } from "@/packages/types";
import type { Id } from "@/packages/types";
import { ChecklistRow } from "./checklist/ChecklistRow";
import { ChecklistFilterBar } from "./checklist/ChecklistFilterBar";
import { ChecklistDetailSheet } from "./checklist/ChecklistDetailSheet";
import {
  CHECKLIST_FILTER_CATEGORIES,
  type FilterCategoryGroupKey,
} from "./checklist/checklist-filter-data";
import type { FilterCategoryGroup } from "@/components/ui";

// ─── Summary Strip ──────────────────────────────────────────────────────────

interface SummaryStripProps {
  stats: ChecklistStats;
}

function SummaryStrip({ stats }: SummaryStripProps) {
  const { t } = useTranslation("audits");
  const { total, approved, uploaded, missing, progress } = stats;

  const statItems = [
    { label: t("checklist.stats.total"), value: total, color: "text-primary" },
    {
      label: t("checklist.stats.approved"),
      value: approved,
      color: "text-emerald-500",
    },
    {
      label: t("checklist.stats.uploaded"),
      value: uploaded,
      color: "text-accent",
    },
    {
      label: t("checklist.stats.missing"),
      value: missing,
      color: "text-muted",
    },
  ];

  return (
    <motion.div
      className="rounded-xl bg-card p-4 mb-3"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.snappy}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4">
          {statItems.map((s) => (
            <div key={s.label} className="flex items-center gap-1.5">
              <span className={`text-lg font-semibold ${s.color}`}>
                {s.value}
              </span>
              <span className="text-[11px] text-muted">{s.label}</span>
            </div>
          ))}
        </div>
        <span className="text-xs text-secondary font-medium">
          {t("checklist.stats.progressApproved", { progress })}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-surface overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ ...springs.smooth, delay: 0.2 }}
          style={{
            backgroundColor:
              progress === 100
                ? "#22c55e"
                : progress >= 50
                  ? "#E8734A"
                  : "#a1a1a6",
          }}
        />
      </div>
    </motion.div>
  );
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface AuditChecklistTabProps {
  auditId: Id<"audits">;
  initialChecklistItemId?: string;
}

// ─── Resolve labelKey → label ───────────────────────────────────────────────

function resolveFilterCategories(
  groups: FilterCategoryGroupKey[],
  t: (key: string) => string,
): FilterCategoryGroup[] {
  return groups.map((group) => ({
    label: t(group.labelKey),
    categories: group.categories.map((cat) => ({
      id: cat.id,
      label: t(cat.labelKey),
      icon: cat.icon,
      allowedOperators: cat.allowedOperators,
      values: cat.values.map((val) => ({
        id: val.id,
        label: t(val.labelKey),
        ...(val.icon && { icon: val.icon }),
        ...(val.color && { color: val.color }),
      })),
    })),
  }));
}

// ─── Outer Component (Filter Provider) ──────────────────────────────────────

export function AuditChecklistTab({
  auditId,
  initialChecklistItemId,
}: AuditChecklistTabProps) {
  const { t } = useTranslation("audits");

  const resolvedCategories = useMemo(
    () =>
      resolveFilterCategories(
        CHECKLIST_FILTER_CATEGORIES,
        (key: string) => t(key as "checklist.filters.all") as string,
      ),
    [t],
  );

  return (
    <Filter categoryGroups={resolvedCategories}>
      <AuditChecklistTabContent
        auditId={auditId}
        initialChecklistItemId={initialChecklistItemId}
      />
    </Filter>
  );
}

// ─── Inner Component (Filter Consumer) ──────────────────────────────────────

function AuditChecklistTabContent({
  auditId,
  initialChecklistItemId,
}: AuditChecklistTabProps) {
  const { t } = useTranslation("audits");
  const { filters: activeFilters } = useFilter();
  const { categories } = useAuditDetail(auditId as Id<"audits">);
  const {
    filteredItems,
    grouped,
    stats,
    searchQuery,
    setSearchQuery,
    activeFilter,
    setActiveFilter,
  } = useChecklistData(categories, activeFilters);

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const isSheetOpen = selectedItemId !== null;

  // All items (before filter) for the filter bar counts
  const allItems = useMemo<UIChecklistItem[]>(
    () => categories?.flatMap((cat) => cat.items) ?? [],
    [categories],
  );

  // Auto-open detail sheet when navigated with a specific checklist item
  useEffect(() => {
    if (initialChecklistItemId && allItems.length > 0) {
      const exists = allItems.some(
        (item) => item.id === initialChecklistItemId,
      );
      if (exists) {
        setSelectedItemId(initialChecklistItemId);
      }
    }
  }, [initialChecklistItemId, allItems]);

  const handleOpenDetail = useCallback((itemId: string) => {
    setSelectedItemId(itemId);
  }, []);

  const handleCloseSheet = useCallback(() => {
    setSelectedItemId(null);
  }, []);

  const handleNavigate = useCallback((itemId: string) => {
    setSelectedItemId(itemId);
  }, []);

  if (!categories || allItems.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Circle size={48} className="text-muted" strokeWidth={1} />
          <p className="text-sm text-muted">{t("checklist.emptyState")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="shrink-0">
        <SummaryStrip stats={stats} />
      </div>

      <div className="shrink-0">
        <ChecklistFilterBar
          items={allItems}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <Filter.PillBar />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={springs.snappy}
        className="flex-1 min-h-0 flex flex-col overflow-y-auto"
        style={{
          opacity: isSheetOpen ? 0.5 : 1,
          pointerEvents: isSheetOpen ? "none" : "auto",
          transition: "opacity 0.2s ease",
        }}
      >
        <List.Provider>
          <List>
            {Array.from(grouped.entries()).map(
              ([categoryId, group], groupIndex) => {
                if (group.items.length === 0) return null;
                const approvedInGroup = group.items.filter(
                  (i) => i.status === "APPROVED",
                ).length;

                return (
                  <List.Group
                    key={categoryId}
                    title={`${group.category.name} (${approvedInGroup}/${group.items.length})`}
                    count={group.items.length}
                    defaultOpen={groupIndex === 0}
                  >
                    {group.items.map((item, i) => (
                      <ChecklistRow
                        key={item.id}
                        item={item}
                        index={i}
                        onOpenDetail={() => handleOpenDetail(item.id)}
                      />
                    ))}
                  </List.Group>
                );
              },
            )}
          </List>
        </List.Provider>
      </motion.div>

      <ChecklistDetailSheet
        item={
          selectedItemId
            ? (filteredItems.find((i) => i.id === selectedItemId) ?? null)
            : null
        }
        open={isSheetOpen}
        onClose={handleCloseSheet}
        filteredItems={filteredItems}
        onNavigate={handleNavigate}
        auditId={auditId}
      />
    </div>
  );
}
